import React, { Fragment, useReducer, useState } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import { Button, Input, Badge, PopoverHeader, PopoverBody, Popover } from 'reactstrap';
import EyeIcon from 'mdi-react/EyeIcon';
import PencilIcon from 'mdi-react/PencilIcon';
// import BinIcon from 'mdi-react/BinIcon';
import { useCustomerUIContext } from '../CustomerUIProvider';
import CustomerTableColumns, { CustomerColumns } from './CustomerTableColumns';
import { CustomerTableSearchBar } from './CustomerTableSearchBar';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getCustomersList, setCustomerFilters } from '../../redux/actions';
import LoadingIcon from 'mdi-react/LoadingIcon';
import { defaultPaginationProps } from '@/shared/helpers';

const CustomerTable = () => {
  const customerUIContext = useCustomerUIContext();
  const dispatch = useDispatch();
  const {
    data: customers,
    error,
    filters,
    loading,
    totalRecords,
  } = useSelector((state) => state.customers, shallowEqual);
  const {
    pageSize: rowsPerPage,
    pageNumber: page,
    sortBy: orderBy,
    isSortOrderDescending,
  } = filters;

  const [popOver, setPopover] = React.useState({});

  React.useEffect(() => {
    dispatch(getCustomersList(filters));
  }, [filters]);

  const handleRequestSort = (event, property) => {
    dispatch(
      setCustomerFilters({ sortBy: property, isSortOrderDescending: !isSortOrderDescending }),
    );
  };

  const handleChangePage = (event, currentPage) => {
    dispatch(setCustomerFilters({ pageSize: rowsPerPage, pageNumber: currentPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch(setCustomerFilters({ pageSize: +event.target.value, pageNumber: 1 }));
  };

  const emptyRows =
    page === Math.ceil(totalRecords / rowsPerPage) ? rowsPerPage - customers.length : 0;

  const renderActionButtons = ({ organizationId: customerId }) => (
    <div className="actions">
      <Button
        className="icon mb-0"
        color="success"
        outline
        onClick={() => customerUIContext.openViewCustomerDialog(customerId)}
      >
        <p>
          <EyeIcon /> View
        </p>
      </Button>
      <Button
        className="icon mb-0"
        color="primary"
        outline
        onClick={() => customerUIContext.openEditCustomerDialog(customerId)}
      >
        <p>
          <PencilIcon /> Edit
        </p>
      </Button>
      {/*            <Button
                className="icon mb-0"
                color="danger" outline
                onClick={()=>customerUIContext.openDeleteCustomerDialog(customerId)}
            >
                <p><BinIcon /> Delete</p>
            </Button>*/}
    </div>
  );

  return (
    <Fragment>
      <div className="material-table__wrap">
        <CustomerTableSearchBar />
        <Table className="material-table">
          <CustomerTableColumns
            order={isSortOrderDescending ? 'desc' : 'asc'}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={rowsPerPage}
          />
          <TableBody>
            {(loading || !!error) && (
              <TableRow>
                <TableCell colSpan={CustomerColumns.length}>
                  {loading && (
                    <div className={'text-center overflow-hidden'}>
                      <div className="panel__refresh position-relative" style={{ height: 75 }}>
                        <LoadingIcon />
                      </div>
                    </div>
                  )}
                  <div className={'text-center'}>
                    <p className={!!error ? 'm-5' : ''}>{error || 'Loading customers...'}</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {!!!customers.length && !loading && !error && (
              <TableRow style={{ height: 120 }}>
                <TableCell colSpan={CustomerColumns.length} align="center">
                  {filters.status === null && filters.search === ''
                    ? 'No Customers'
                    : 'No Customers with applied filters'}
                </TableCell>
              </TableRow>
            )}
            {customers.map((customer) => {
              return (
                <TableRow className="material-table" tabIndex={-1} key={customer?.organizationId}>
                  <TableCell
                    className="material-table__cell"
                    component="th"
                    align="center"
                    scope="row"
                  >
                    {customer?.organizationName}
                  </TableCell>
                  <TableCell align="center" className="material-table__cell">
                    <Badge
                      color={`${customer?.status ? 'success' : 'danger'}`}
                      style={{ marginRight: 24 }}
                    >
                      {customer?.status ? 'Active' : 'Deactivated'}
                    </Badge>
                  </TableCell>
                  <TableCell align="center" className="material-table__cell">
                    {customer?.usersCount}
                  </TableCell>
                  <TableCell align="center" className="material-table__cell">
                    <p
                      id={`assessments_${customer?.organizationId}`}
                      style={{ marginRight: 24, cursor: 'pointer' }}
                      onMouseEnter={() =>
                        setPopover({ [`assessments_${customer?.organizationId}`]: true })
                      }
                      onMouseLeave={() =>
                        setPopover({ [`assessments_${customer?.organizationId}`]: false })
                      }
                    >
                      {' '}
                      {customer?.assessmentAssigned}
                    </p>
                    <Popover
                      placement="bottom"
                      isOpen={!!popOver?.[`assessments_${customer?.organizationId}`]}
                      target={`assessments_${customer?.organizationId}`}
                    >
                      <PopoverHeader>{customer?.organizationName}'s Assessment</PopoverHeader>
                      <PopoverBody>
                        <ul style={{ fontWeight: 'bolder' }}>
                          <li>Pending Assessment : {customer?.assessmentPending}</li>
                          <li>Completed Assessment : {customer?.assessmentCompleted}</li>
                        </ul>
                      </PopoverBody>
                    </Popover>
                  </TableCell>
                  <TableCell
                    align="center"
                    className="material-table__cell"
                    style={{ minWidth: '295px' }}
                  >
                    {renderActionButtons(customer)}
                  </TableCell>
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow style={{ height: 49 * emptyRows }}>
                <TableCell colSpan={CustomerColumns.length} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page - 1}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        component={defaultPaginationProps.component}
        backIconButtonProps={defaultPaginationProps.backIconButton}
        nextIconButtonProps={defaultPaginationProps.nextIconButton}
        rowsPerPageOptions={defaultPaginationProps.rowsPerPage}
        className={defaultPaginationProps.className}
        dir={defaultPaginationProps.dir}
      />
    </Fragment>
  );
};

export default CustomerTable;
